import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Title, Meta, MetaDefinition } from '@angular/platform-browser';

import { filter } from 'rxjs/operators';

import { Config } from '../config';
import { ContentService } from '../content';
import { PageItem } from '../models';

import {
    LanguageMap,
    LinkAttribute,
    SiteConfig,
} from '@src/app/shared/utils/models';

type MetaFields = Partial<
    Pick<PageItem, 'meta-title' | 'page-title' | 'meta-description'>
>;

const SITE_NAME = 'Vision Fitness';

@Injectable({
    providedIn: 'root',
})
export class MetaService {
    constructor(
        @Inject(Config) private config: Config,
        @Inject(DOCUMENT) private document: Document,
        private contentService: ContentService,
        private title: Title,
        private router: Router,
        private meta: Meta,
    ) {}

    init(): void {
        this.router.events
            .pipe(
                filter(
                    (event): event is NavigationEnd =>
                        event instanceof NavigationEnd,
                ),
            )
            .subscribe((event) => {
                if (this.isNotFound()) {
                    this.clearPageMeta();
                    return;
                }

                const path = this.getPathWithoutRegLang(
                    event.url.split(/[?#]/)[0],
                    this.config.region,
                );
                const resolvedMeta = this.resolveMeta(path);

                this.updateSiteTitle(resolvedMeta['meta-title']);
                this.updateMetaDescription(resolvedMeta['meta-description']);
                this.updateHrefLangLinks(event.url);
                this.updateCanonical();
            });
    }

    //the order of sources is important for cascading from specific to general meta info
    private resolveMeta(path: string): MetaFields {
        const sources: MetaFields[] = [
            ...this.getResolvedRouteData(),
            this.contentService.matchPage(path),
        ].filter((source): source is MetaFields => !!source);

        const titleOf = (source: MetaFields): string | undefined =>
            source['meta-title'] || source['page-title'];

        return {
            'meta-title': sources.map(titleOf).find(Boolean),
            'meta-description': sources.find(
                (source) => source['meta-description'],
            )?.['meta-description'],
        };
    }

    private getResolvedRouteData(): MetaFields[] {
        const levels: MetaFields[][] = [];
        let route: ActivatedRouteSnapshot | null =
            this.router.routerState.snapshot.root;

        while (route) {
            levels.unshift(
                Object.values(route.data).filter(
                    (value) =>
                        !!value &&
                        typeof value === 'object' &&
                        !Array.isArray(value),
                ),
            );
            route = route.firstChild;
        }

        return levels.flat();
    }

    private isNotFound(): boolean {
        let route: ActivatedRouteSnapshot =
            this.router.routerState.snapshot.root;

        while (route.firstChild) {
            route = route.firstChild;
        }

        return !!route.data['notFound'];
    }

    private clearPageMeta(): void {
        this.title.setTitle(SITE_NAME);
        this.meta.removeTag('name="description"');
        this.document
            .querySelectorAll('link[rel="canonical"], link[rel="alternate"]')
            .forEach((link) => link.remove());
    }

    // CMS titles may already end with the site name, so only append it when missing
    private updateSiteTitle(pageTitle?: string): void {
        const title = pageTitle?.trim();

        if (!title) {
            this.title.setTitle(SITE_NAME);
            return;
        }

        const hasSiteName = title
            .toLowerCase()
            .endsWith(SITE_NAME.toLowerCase());

        this.title.setTitle(hasSiteName ? title : `${title} | ${SITE_NAME}`);
    }

    private cleanUrl(segment: string) {
        return segment ? segment.replace(/[#?].*/, '') : segment;
    }

    getUrlSlugs(url: string, region: string) {
        const segments = url.split('/');

        if (region !== 'world') {
            segments.shift();
        }

        const slugsArray = [
            this.cleanUrl(segments[2]) || 'HOMEPAGE',
            this.cleanUrl(segments[3]) || '',
            this.cleanUrl(segments[4]) || '',
            this.cleanUrl(segments[5]) || '',
        ];

        return slugsArray;
    }

    private getPathWithoutRegLang(url: string, region: string) {
        const segments = url.split('/');

        if (region !== 'world') {
            segments.shift();
        }

        url = segments.splice(2).join('/');

        if (url.charAt(0) !== '/') {
            url = '/' + url;
        }

        return url;
    }

    private updateMetaDescription(metaDescription?: string): void {
        if (metaDescription) {
            this.setMetaDescription({
                name: 'description',
                content: metaDescription,
            });
        } else {
            this.meta.removeTag('name="description"');
        }
    }

    private setMetaDescription(metaDescriptionDef: MetaDefinition): void {
        if (!this.meta.getTag(`name='description'`)) {
            this.meta.addTag(metaDescriptionDef, true);
        } else {
            this.meta.updateTag(metaDescriptionDef, `name='description'`);
        }
    }

    private updateHrefLangLinks(url: string): void {
        const links = this.document.querySelectorAll<HTMLLinkElement>(
            'link[rel="alternate"]',
        );

        const siteConfigs: Record<string, SiteConfig> = this.config.sites;

        if (!links.length) {
            const url = new URL(this.document.URL);
            const host = url.hostname;
            const protocol = url.protocol;

            const baseUrl = protocol + '//' + host;
            const hrefLink = this.generateHrefLangLinks(
                this.router.url,
                baseUrl,
                siteConfigs,
            );
            this.addHrefLinksToDOM(hrefLink);
        }

        const langRegUrlRegex = /^(\/[\w]{2})?\/([\w]{3})+/;
        const path = url.replace(langRegUrlRegex, '').replace(/^\//, '');
        for (let i = 0; i < links.length; i++) {
            const urlTree = this.router.createUrlTree([links[i].href]);
            const [protocol, domain, region, language] =
                urlTree.root.children.primary.segments
                    .map((segment) => segment.path)
                    .slice(0, 4);

            const newLink = region.match(/[\w]{3}/)
                ? [domain, region, null, path]
                : [domain, region, language, path];

            links[i].href =
                protocol +
                '//' +
                newLink.join('/').replace(/\/\//g, '/').replace(/\/$/, '');
        }
    }

    private addHrefLinksToDOM(linkTags: LinkAttribute[]) {
        linkTags.forEach((items) => {
            const linkTag: HTMLLinkElement =
                this.document.createElement('link');
            linkTag.rel = 'alternate';
            linkTag.href = items.href;
            linkTag.hreflang = items.hrefLang;
            this.document.getElementsByTagName('head')[0].appendChild(linkTag);
        });
    }

    private updateCanonical(): void {
        const links = this.document.getElementsByTagName('link');

        for (let i = 0; i < links.length; i++) {
            if (links[i].getAttribute('rel') === 'canonical') {
                links[i].remove();
            }
        }

        let link: HTMLLinkElement = this.document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', this.document.URL);
        this.document.head.appendChild(link);
    }

    generateHrefLangLinks = (
        path: any,
        baseUrl: string,
        siteConfig: Record<string, SiteConfig>,
    ) => {
        let linkTags: LinkAttribute[] = [];

        let splitPath = path.split('/');
        if (splitPath[1] === 'eng' || splitPath[1] === 'esp') {
            path = splitPath.slice(2).join('/');
        } else {
            path = splitPath.slice(3).join('/');
        }
        const regions = Object.values(siteConfig);
        regions.forEach((region) => {
            const languages = Object.entries(region.languages).map((e) => ({
                [e[0]]: e[1],
            }));
            let langs = languages
                .filter((lang) => !lang[Object.keys(lang)[0]]['hidden'])
                .map((filteredLang) => Object.keys(filteredLang));
            let splitLangs = langs.map((lang) => lang[0].split('-')[0]);
            let uniqueLangs = [...new Set(splitLangs)];
            uniqueLangs.forEach((language) => {
                let href: string;
                let hrefLang: string;
                if (region.id === 'world') {
                    href = `${baseUrl}/${language}/${path}`;
                    hrefLang = 'x-default';
                } else {
                    href = `${baseUrl}/${region.id}/${language}/${path}`;
                    let languageCode =
                        LanguageMap[
                            language.toUpperCase() as keyof typeof LanguageMap
                        ].toLowerCase();
                    hrefLang = `${languageCode}-${region.id}`;
                }
                linkTags.push(new LinkAttribute(href, hrefLang));
            });
        });
        return linkTags;
    };
}
